/**
 * A sparse grid of monospace glyphs, mostly near-invisible, with a few cells typing themselves in or
 * flickering to the accent. The site is already a terminal, so this reads as code without being a
 * picture of code the way the photograph it replaced was.
 *
 * The field stirs where the pointer is: cells under it wake far more often, falling back to their own
 * pace with distance. The pointer is felt in the animation rather than painted as a light over it.
 *
 * The resting grid is painted once into an offscreen canvas and blitted each frame, because repainting
 * every glyph at 30fps meant tens of thousands of fillText calls a second for a backdrop nobody looks
 * at directly. Only the handful of awake cells are drawn per frame; a cell repaints itself into the
 * resting layer when it falls asleep and picks its next glyph.
 */

const kDefaultCellSize = 26;
/**
 * Kept to ASCII, which Consolas and Courier New both render at one fixed width - anything outside it
 * risks a fallback font and a glyph that does not sit in its cell. The mix is deliberate: the heavy
 * marks carry the field and the thin ones let it breathe, so the texture does not read as one weight.
 */
const kGlyphs = [
	'{', '}', '<', '>', '(', ')', '[', ']',
	';', ':', ',', '.', '\'', '"', '`', '|',
	'/', '\\', '-', '_', '=', '+', '*', '~',
	'!', '?', '%', '^', '&', '@', '#', '$',
	'0', '1', '2', '7', 'A', 'E', 'F', 'x'
];
/** Roughly one cell in this many wakes per second. */
const kWakeRate = 0.012;
const kRestAlpha = 0.13;
/** How much brighter an awake cell gets on top of the resting alpha. */
const kAwakeAlpha = 0.55;
/** Glyph size as a share of the cell, and what is left over becomes the inset that centres it. */
const kGlyphScale = 0.62;
/** How many times faster a cell directly under the pointer wakes. */
const kPointerBoost = 14;

class GlyphField {
	/**
	 * GlyphField initialization.
	 */
	constructor (cellSize) {
		this.cellSize = typeof (cellSize) !== 'undefined' ? cellSize : kDefaultCellSize;
		this.glyphSize = Math.round (this.cellSize * kGlyphScale);
		this.glyphInset = Math.round ((this.cellSize - this.glyphSize) / 2);
		this.columns = 0;
		this.rows = 0;
		this.cells = [];
		this.width = 0;
		this.height = 0;
		this.scale = 1;
		this.restingCanvas = null;
		this.restingContext = null;
		this.restingColors = null;
		this.pointerColumn = null;
		this.pointerRow = null;
		this.pointerCells = 0;
	}

	/**
	 * Take the pointer position, in css pixels, as grid coordinates. Null means the pointer is away.
	 */
	SetPointer (pointer, radius) {
		if (pointer === null) {
			this.pointerColumn = null;
			this.pointerRow = null;
			return;
		}

		this.pointerColumn = pointer.x / this.cellSize;
		this.pointerRow = pointer.y / this.cellSize;
		this.pointerCells = radius / this.cellSize;
	}

	/**
	 * How much faster this cell should wake, given where the pointer is. 1 is its own pace.
	 * Squared falloff, so the effect gathers under the pointer instead of spreading thinly.
	 */
	PointerFactor (index) {
		if (this.pointerColumn === null) {
			return 1;
		}

		const dx = (index % this.columns) - this.pointerColumn;
		const dy = Math.floor (index / this.columns) - this.pointerRow;
		const squared = dx * dx + dy * dy;

		//Compared squared to keep the root off the cells that are nowhere near.
		if (squared > this.pointerCells * this.pointerCells) {
			return 1;
		}

		const falloff = 1 - Math.sqrt (squared) / this.pointerCells;

		return 1 + kPointerBoost * falloff * falloff;
	}

	/**
	 * Rebuild the grid, and the resting layer with it, for a new canvas size.
	 */
	Resize (width, height, scale) {
		this.columns = Math.ceil (width / this.cellSize);
		this.rows = Math.ceil (height / this.cellSize);
		this.width = width;
		this.height = height;
		this.scale = scale;

		this.cells = [];
		for (let index = 0; index < this.columns * this.rows; index++) {
			this.cells.push ({
				glyph: kGlyphs [Math.floor (Math.random () * kGlyphs.length)],
				//Negative means asleep; the value counts up to 1 while a cell is awake.
				life: -Math.random () * 40,
				accent: Math.random () < 0.4
			});
		}

		this.restingCanvas = document.createElement ('canvas');
		this.restingCanvas.width = Math.floor (width * scale);
		this.restingCanvas.height = Math.floor (height * scale);
		this.restingContext = this.restingCanvas.getContext ('2d');
		this.restingContext.setTransform (scale, 0, 0, scale, 0, 0);

		//Forces a repaint of the resting layer on the next draw, when the colors are known.
		this.restingColors = null;
	}

	/**
	 * Draw one frame.
	 */
	Draw (context, deltaSeconds, colors, animated) {
		//The scheme can change under us, and the resting layer is baked with the old color.
		if (this.restingColors === null || this.restingColors.text !== colors.text || this.restingColors.accent !== colors.accent) {
			this.PaintResting (colors);
		}

		context.drawImage (this.restingCanvas, 0, 0, this.width, this.height);

		context.font = this.Font ();
		context.textBaseline = 'top';

		for (let index = 0; index < this.cells.length; index++) {
			const cell = this.cells [index];

			if (animated) {
				this.Advance (cell, index, deltaSeconds, colors);
			}

			if (cell.life < 0) {
				continue;
			}

			//A cell fades in over the first third of its life and back out over the last third.
			const emphasis = cell.life < 0.33 ? cell.life / 0.33 : (cell.life > 0.66 ? (1 - cell.life) / 0.34 : 1);

			context.fillStyle = cell.accent ? colors.accent : colors.text;
			context.globalAlpha = Math.min (1, kRestAlpha + emphasis * kAwakeAlpha);

			this.FillGlyph (context, cell, index);
		}

		context.globalAlpha = 1;
	}

	/**
	 * Paint every cell into the resting layer at its dim alpha.
	 */
	PaintResting (colors) {
		this.restingContext.clearRect (0, 0, this.width, this.height);
		this.restingContext.font = this.Font ();
		this.restingContext.textBaseline = 'top';
		this.restingContext.fillStyle = colors.text;
		this.restingContext.globalAlpha = kRestAlpha;

		for (let index = 0; index < this.cells.length; index++) {
			this.FillGlyph (this.restingContext, this.cells [index], index);
		}

		this.restingColors = {text: colors.text, accent: colors.accent};
	}

	/**
	 * Repaint one cell of the resting layer, after it changed glyph.
	 */
	PaintRestingCell (index, colors) {
		const column = index % this.columns;
		const row = Math.floor (index / this.columns);

		this.restingContext.clearRect (column * this.cellSize, row * this.cellSize, this.cellSize, this.cellSize);
		this.restingContext.fillStyle = colors.text;
		this.restingContext.globalAlpha = kRestAlpha;

		this.FillGlyph (this.restingContext, this.cells [index], index);
	}

	/**
	 * Draw one cell's glyph at its grid position.
	 */
	FillGlyph (context, cell, index) {
		const column = index % this.columns;
		const row = Math.floor (index / this.columns);

		context.fillText (cell.glyph, column * this.cellSize + this.glyphInset, row * this.cellSize + this.glyphInset);
	}

	/**
	 * The one font string both layers draw with.
	 */
	Font () {
		return `${this.glyphSize}px Consolas, 'Courier New', monospace`;
	}

	/**
	 * Move one cell through its sleep, wake and fade cycle. A cell picks its next glyph as it falls
	 * asleep rather than as it wakes, so the resting layer and the awake cell never disagree.
	 */
	Advance (cell, index, deltaSeconds, colors) {
		if (cell.life < 0) {
			cell.life += deltaSeconds * kWakeRate * 120 * this.PointerFactor (index);
			return;
		}

		cell.life += deltaSeconds * 0.35;

		if (cell.life < 1) {
			return;
		}

		cell.life = -2 - Math.random () * 40;
		cell.glyph = kGlyphs [Math.floor (Math.random () * kGlyphs.length)];
		cell.accent = Math.random () < 0.4;

		this.PaintRestingCell (index, colors);
	}
}

export default GlyphField;
